import { useEffect, useRef } from 'react';

function buildSearchString(params) {
  const search = params.toString();
  return search ? `?${search}` : '';
}

function findByUrl(conversations, params) {
  const v2Uuid = params.get('conversation');
  if (v2Uuid) return conversations.find((c) => c.conversation_id === v2Uuid) || null;

  const v1Id = params.get('v1_conversation_id');
  if (v1Id) return conversations.find((c) => String(c.migrate_id) === v1Id) || null;

  return null;
}

/**
 * Turbolinks 5 initializes history entries with `{ turbolinks: { restorationIdentifier } }`.
 * Our SPA pushes `history.pushState/replaceState` with `{}` so TL ignores those entries—but if the
 * initial load URL already matched (?conversation=<uuid>) we exited early without replacing state,
 * leaving TL metadata on stack. Browser back then lets TL handle popstate → restore/invalidate/reload (V1).
 */
function stripTurbolinksHistoryStateIfPresent() {
  if (typeof window === 'undefined') return;
  if (!window.history.state?.turbolinks) return;

  const canonical = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.history.replaceState({}, '', canonical);
}

/**
 * URL ↔ selection sync for both selection kinds — ?conversation=<uuid> and ?pitch=<v2 id>
 * (virtual pitch rows), which are mutually exclusive upstream. pushState on select,
 * popstate listener for back/forward, replaceState on first canonicalization of a
 * v1_conversation_id deep-link.
 */
function useUrlReflection({
  selectedConversation,
  conversations,
  selectConversation,
  selectedPitch,
  pitchRows,
  selectPitch,
}) {
  const isFirstSelectionRef = useRef(true);
  // Skip URL writes until the first non-null selection — otherwise we'd strip
  // the inbound deep-link param before useConversations has had a chance to read it.
  const hasSeenSelectionRef = useRef(false);

  // Write URL when selection changes. The "URL already matches" early-return
  // keeps popstate→reselect from pushing a duplicate history entry.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!selectedConversation && !selectedPitch && !hasSeenSelectionRef.current) return;
    if (selectedConversation || selectedPitch) hasSeenSelectionRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const currentUuidInUrl = params.get('conversation');
    const currentPitchInUrl = params.get('pitch');
    const desiredUuid = selectedConversation?.conversation_id ?? null;
    const desiredPitchId = selectedPitch ? String(selectedPitch.id) : null;

    if (currentUuidInUrl === desiredUuid && currentPitchInUrl === desiredPitchId && !params.has('v1_conversation_id')) {
      isFirstSelectionRef.current = false;
      stripTurbolinksHistoryStateIfPresent();
      return;
    }

    if (desiredUuid) {
      params.set('conversation', desiredUuid);
    } else {
      params.delete('conversation');
    }
    if (desiredPitchId) {
      params.set('pitch', desiredPitchId);
    } else {
      params.delete('pitch');
    }
    params.delete('v1_conversation_id');

    const newUrl = `${window.location.pathname}${buildSearchString(params)}${window.location.hash}`;

    if (isFirstSelectionRef.current) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
    isFirstSelectionRef.current = false;
  }, [selectedConversation, selectedPitch]);

  // Effect 2: react to back/forward by re-selecting per the new URL.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const target = findByUrl(conversations, params);

      if (target) {
        selectConversation(target);
        return;
      }

      const pitchParam = params.get('pitch');
      if (pitchParam && selectPitch) {
        const pitch = (pitchRows || []).find((p) => String(p.id) === pitchParam);
        // Selecting via popstate never marks read — selectPitch is the quiet variant.
        // Not loaded → usePitchDeepLink's popstate listener resolves it directly.
        if (pitch) selectPitch(pitch);
        return;
      }

      // Not in this list slice yet — useDeepLinkResolver handles popstate with findById
      // (other mailbox on V1, Turbolinks + history quirks, transient loading).
      const hasConversationInUrl = Boolean(params.get('conversation')) || Boolean(params.get('v1_conversation_id'));
      if (hasConversationInUrl) {
        // Not in this list slice yet — useDeepLinkResolver handles popstate with findById
        // (other mailbox on V1, Turbolinks + history quirks, transient loading).
        return;
      }

      if (selectedConversation) {
        selectConversation(null);
      }
      if (selectedPitch && selectPitch) {
        selectPitch(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [conversations, selectConversation, selectedConversation, pitchRows, selectPitch, selectedPitch]);
}

export default useUrlReflection;
